<template>
  <v-row>
    <v-col cols="12">
      <v-form ref="crudForm" @submit.prevent="submit">
        <v-row>
          <v-col cols="12" md="4" order-md="last">
            <MediaSelector directory="Location" v-model="model.PhotoId" :disabled="disableAll" />
          </v-col>
          <v-spacer />
          <v-col cols="12" md="6" order-md="first">
            <v-row>
              <v-col cols="12">
                <v-text-field label="Location Name"
                              v-model="model.Title"
                              maxlength="128"
                              :rules="rules.Title"
                              :disabled="disableAll" />
              </v-col>
              <v-col cols="12">
                <v-text-field class="no-spinner"
                              label="Epic Dept. No"
                              type="number"
                              maxlength="64"
                              v-model="model.EpicDepartmentId"
                              :disabled="disableAll" />
              </v-col>
              <v-col cols="12">
                <v-textarea label="Notes"
                            v-model="model.Notes"
                            outlined
                            :disabled="disableAll" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field label="Address 1"
                          maxlength="64"
                          v-model="model.Address1"
                          :disabled="disableAll" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field label="Address 2"
                          maxlength="64"
                          v-model="model.Address2"
                          :disabled="disableAll" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field label="City"
                          maxlength="64"
                          v-model="model.City"
                          :disabled="disableAll" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field label="State"
                          v-model="model.State"
                          v-mask="model.Country === 'US' ? masks.state : masks.noMask"
                          :disabled="disableAll"
                          :maxlength="model.Country === 'US' ? 2 : 64" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field label="Postal Code"
                          v-model="model.Zip"
                          v-mask="model.Country === 'US' ? masks.zip : masks.noMask"
                          :disabled="disableAll"
                          :maxlength="model.Country === 'US' ? 5 : 64" />
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete label="Country"
                            append-icon="add"
                            item-text="Title"
                            item-value="Code"
                            prepend-icon="public"
                            v-model="model.Country"
                            :rules="rules.Country"
                            :items="countries.items"
                            :loading="countries.loading"
                            @keydown.enter.native.prevent="">
              <template v-slot:prepend-item>
                <v-list-item v-if="$store.getters.can('Admin')"
                             ripple
                             @click="$refs.CountryForm.open()">
                  <v-list-item-icon>
                    <v-icon>add</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    Create New...
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-text-field label="Primary Phone Number"
                          type="tel"
                          v-mask="masks.phone"
                          v-model="model.Phone"
                          :disabled="disableAll" />
          </v-col>
          <v-col cols="6">
            <v-text-field label="Secondary Phone Number"
                          type="tel"
                          v-mask="masks.phone"
                          v-model="model.SecondaryPhone"
                          :disabled="disableAll" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-checkbox v-model="model.AutoGeocode">
              <template v-slot:label>
                Automatic Geocoding
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon dense
                            class="ml-2"
                            v-on="on">help</v-icon>
                  </template>
                  Disable this only if you know the Latitude
                  and Longitude of this location
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field class="no-spinner"
                          label="Latitude"
                          type="number"
                          v-model="model.Latitude"
                          :disabled="disableAll || model.AutoGeocode" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field class="no-spinner"
                          label="Longitude"
                          type="number"
                          v-model="model.Longitude"
                          :disabled="disableAll || model.AutoGeocode" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <h2>Scheduling</h2>
              </v-col>
              <v-col cols="12" md="6">
                <v-select clearable
                          label="Primary Schedule"
                          v-model="model.ScheduleId"
                          item-text="Title"
                          item-value="Id"
                          :loading="schedules.loading"
                          :items="schedules.items">
                  <template v-slot:prepend-item>
                    <v-list-item v-if="$store.getters.can('Admin')"
                                 ripple
                                 @click="openScheduleCreator(model, 'ScheduleId')">
                      <v-list-item-icon>
                        <v-icon>add</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        Create New...
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider />
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select clearable
                          label="Emergency Schedule"
                          v-model="model.EmergencyScheduleId"
                          item-text="Title"
                          item-value="Id"
                          :loading="schedules.loading"
                          :items="schedules.items">
                  <template v-slot:prepend-item>
                    <v-list-item v-if="$store.getters.can('Admin')"
                                 ripple
                                 @click="openScheduleCreator(model, 'EmergencyScheduleId')">
                      <v-list-item-icon>
                        <v-icon>add</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        Create New...
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider />
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="8">
            <v-row>
              <v-col cols="12">
                <h2>Offices</h2>
              </v-col>
              <Repeater id="hours"
                        v-model="model.Hours"
                        :disabled="disableAll"
                        :model="getOfficeModel()">
                <template v-slot:item="{ item }">
                  <v-col cols="12">
                        <v-row>
                          <v-col cols="12" md="6">
                              <v-text-field label="Title"
                                            v-model="item.Title"
                                            :disabled="disableAll" />

                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field label="Phone"
                                          v-model="item.Phone"
                                          v-mask="masks.phone"
                                          :disabled="disableAll" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field label="Fax"
                                          v-model="item.PhoneAlt"
                                          v-mask="masks.phone"
                                          :disabled="disableAll" />

                          </v-col>

                        </v-row>
                  </v-col>
                  <v-col cols="12">
                        <v-text-field label="Description"
                                      v-model="item.Description"
                                      :disabled="disableAll" />

                  </v-col>
                  <v-col cols="12">
                    <v-row>
                      <v-col cols="12" md="9">
                          <v-select clearable
                                    label="Office Schedule"
                                    v-model="item.ScheduleId"
                                    item-text="Title"
                                    item-value="Id"
                                    :loading="schedules.loading"
                                    :items="schedules.items">
                            <template v-slot:prepend-item>
                              <v-list-item v-if="$store.getters.can('Admin')"
                                          ripple
                                          @click="openScheduleCreator(item, 'ScheduleId')">
                                <v-list-item-icon>
                                  <v-icon>add</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  Create New...
                                </v-list-item-content>
                              </v-list-item>
                              <v-divider />
                            </template>
                          </v-select>
                        </v-col>
                        <v-col cols="12" md="3">
                                <v-text-field label="Suite"
                                              v-model="item.Suite"
                                              :disabled="disableAll" />

                        </v-col>
                    </v-row>
                  </v-col>
                </template>
              </Repeater>
            </v-row>
          </v-col>
          <v-col cols="12" md="4">
            <v-row>
              <v-col cols="12">
                <h2>Categories</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Categories"
                            :loading="tags.loading"
                            :items="tags.items.Category"
                            :disabled="disableAll"
                            v-model="model.Categories">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <h2>Designations</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Designations"
                            :loading="tags.loading"
                            :disabled="disableAll"
                            :items="tags.items.Designation"
                            v-model="model.Designations">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <h2>Services</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Services"
                            :loading="tags.loading"
                            :disabled="disableAll"
                            :items="tags.items.Service"
                            v-model="model.Services">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-if="$store.getters.can('Editor')">
          <v-col cols="12">
            <h2>
              Location Visibility
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon class="ml-2" v-on="on">help</v-icon>
                </template>
                <ul>
                  <li>Draft: New Location In Progress</li>
                  <li>Submitted: Location Complete. Awaiting Approval</li>
                  <li>Published: Available on Carle.org</li>
                </ul>
              </v-tooltip>
            </h2>
          </v-col>
          <v-col cols="12" md="auto">
            <v-select class="select-status"
                      v-model="model.Status"
                      outlined
                      :disabled="disableAll"
                      :items="statusOptions" />
          </v-col>
          <v-col cols="12" md="auto">
            <v-switch v-model="model.Active">
              <template v-slot:label>
                {{ model.Active ? 'Active' : 'Inactive' }}
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon class="ml-2" dense v-on="on">help</v-icon>
                  </template>
                  <ul>
                    <li>
                      Active: Location is active in the Carle Network
                    </li>
                    <li>
                      Inactive: Location is no longer active in the Carle Network
                    </li>
                  </ul>
                </v-tooltip>
              </template>
            </v-switch>
          </v-col>
          <v-col cols="12" md="auto">
            <v-switch v-model="model.HideLocation">
              <template v-slot:label>
                {{ model.HideLocation ? 'Hide Location' : 'Show Location' }}
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon class="ml-2" dense v-on="on">help</v-icon>
                  </template>
                  <ul>
                    <li>
                      Hide Location: not listed in the Locations list on the Carle Website
                    </li>
                    <li>
                      Show Location: listed in the Locations list on the Carle Website
                    </li>
                  </ul>
                </v-tooltip>
              </template>
            </v-switch>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h2>
              Preferred Parking
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon class="ml-2" v-on="on">help</v-icon>
                </template>
                Enter preferred parking areas for this location.
                This is a WYSIWYG editor, so it does accept rich text.
              </v-tooltip>
            </h2>
          </v-col>
          <v-col cols="12">
            <tiptap-vuetify v-model="model.PrefParking" :extensions="extensions" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h2>
              Location Body Copy
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon class="ml-2" v-on="on">help</v-icon>
                </template>
                This is an open-ended content field to add body copy to the location.
                This is a WYSIWYG editor, so it does accept rich text.
              </v-tooltip>
            </h2>
          </v-col>
          <v-col cols="12">
            <tiptap-vuetify v-model="model.LocationBody" :extensions="extensions" />
          </v-col>
        </v-row>
        <v-row v-if="$store.getters.can('Editor')">
          <v-col cols="auto">
            <v-btn type="submit" color="primary" :loading="loading">
              {{ id ? 'Save' : 'Create' }}
            </v-btn>
          </v-col>
          <v-col cols="auto" v-if="id">
            <v-btn outlined
                   color="primary"
                   target="_blank"
                   :href="previewLink">Preview</v-btn>
          </v-col>
        </v-row>
        <v-row v-else-if="!disableAll">
          <v-col cols="auto">
            <v-btn outlined color="primary" type="submit">Save Draft</v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" @click="$refs.confirm.open()">Submit for Review</v-btn>
          </v-col>
          <v-col cols="auto" v-if="id">
            <v-btn outlined
                   color="primary"
                   target="_blank"
                   :href="previewLink">Preview</v-btn>
          </v-col>
        </v-row>
      </v-form>
      <NestedForm form="CountryForm"
                  title="Create Country"
                  ref="CountryForm"
                  v-if="$store.getters.can('Admin')"
                  @update="addNewCountry" />
      <NestedForm form="ScheduleForm"
                  title="Create Schedule"
                  ref="ScheduleForm"
                  v-if="$store.getters.can('Admin')"
                  @update="addNewSechedule" />
      <v-row justify="center" align="center">
        <ConfirmDialog ref="confirm"
                       title="WARNING"
                       width="450"
                       @continue="saveAndSubmit">
          You are about to submit this Location for review.<br>
          This form will be put into readonly mode pending approval from an Editor or Administrator.
          <br><br>
          <strong>Are you sure you would like to proceed?</strong>
        </ConfirmDialog>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mask } from 'vue-the-mask';
import FormMixins from '@/components/Mixins/Forms';
import { rules } from '@/util/Helpers';
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify';

export default {
  name: 'LocationForm',
  components: { TiptapVuetify },
  directives: {
    mask,
  },
  mixins: [FormMixins],
  props: {
    Id: [String, Number],
    Title: String,
    Slug: String,
    Address1: String,
    Address2: String,
    City: String,
    State: String,
    Zip: String,
    PrefParking: String,
    LocationBody: String,
    Country: {
      type: String,
      default: 'United States',
    },
    Latitude: {
      type: Number,
      default: 0,
    },
    Longitude: {
      type: Number,
      default: 0,
    },
    AutoGeocode: {
      type: Boolean,
      default: true,
    },
    Phone: String,
    SecondaryPhone: String,
    Notes: String,
    EpicDepartmentId: [String, Number],
    PhotoId: [String, Number],
    ScheduleId: [String, Number],
    EmergencyScheduleId: [String, Number],
    Active: Boolean,
    Hours: Array,
    Categories: Array,
    Designations: Array,
    Services: Array,
    Status: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      id: this.Id,
      model: {
        Title: this.Title,
        Slug: this.Slug,
        Address1: this.Address1,
        Address2: this.Address2,
        City: this.City,
        State: this.State,
        Zip: this.Zip,
        Country: this.Country,
        Latitude: this.Latitude,
        Longitude: this.Longitude,
        Phone: this.Phone,
        Notes: this.Notes,
        EpicDepartmentId: this.EpicDepartmentId,
        PhotoId: this.PhotoId,
        ScheduleId: this.ScheduleId,
        EmergencyScheduleId: this.EmergencyScheduleId,
        Active: this.Active,
        Hours: this.Hours,
        Categories: this.Categories,
        PrefParking: this.PrefParking,
        LocationBody: this.LocationBody,
        Designations: this.Designations,
        Services: this.Services,
        Status: this.Status,
        AutoGeocode: this.AutoGeocode,
        HideLocation: this.HideLocation,
        SecondaryPhone: this.SecondaryPhone,
      },
      rules: {
        Title: [
          (v) => rules.required(v, 'Please enter a Place Name'),
        ],
        Country: [
          (v) => rules.required(v, 'Please select a Country'),
        ],
      },
      loading: false,
      countries: {
        loading: true,
        items: [],
      },
      tags: {
        loading: true,
        items: [],
      },
      schedules: {
        loading: true,
        items: [],
        tmp: null,
      },
      masks: {
        phone: '(###) ###-####',
        state: 'AA',
        zip: '#####',
        noMask: {
          mask: '*'.repeat(64),
          tokens: {
            '*': { pattern: /[a-zA-Z0-9\s]/ },
          },
        },
      },
      statusOptions: [
        { value: 0, text: 'Draft' },
        { value: 1, text: 'Submitted' },
        { value: 2, text: 'Published' },
      ],
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading,
          {
            options: {
              levels: [1, 2, 3],
            },
          },
        ],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
    };
  },
  computed: {
    disableAll() {
      return !this.canEdit();
    },
    previewLink() {
      const token = this.$store.getters.user.access_token;
      return `${process.env.VUE_APP_PREVIEW}/locations/${this.model.Slug}?preview=${token}`;
    },
  },
  methods: {
    canEdit() {
      return this.$store.getters.can('Editor') || this.model.Status === 0;
    },
    async submit() {
      if (this.$refs.crudForm.validate()) {
        this.loading = true;
        if (this.id) {
          await this.update();
        } else {
          await this.create();
        }
        this.loading = false;
      } else {
        this.$refs.crudForm.inputs.every(this.goToFirstError);
      }
    },
    saveAndSubmit() {
      this.model.Status = 1;
      this.submit();
    },
    async create() {
      try {
        const response = await this.$store.dispatch('post', {
          api: 'Location',
          data: this.model,
        });
        this.$emit('snackbar', 'Location successfully created!');
        this.id = response.data.Id;
        this.model.Slug = response.data.Slug;
        this.model.Latitude = response.data.Latitude;
        this.model.Longitude = response.data.Longitude;
      } catch (e) {
        // Do nothing
      }
    },
    async update() {
      try {
        const response = await this.$store.dispatch('put', {
          api: `Location/${this.id}`,
          data: this.model,
        });
        this.$emit('snackbar', 'Location successfully updated!');
        this.model.Slug = response.data.Slug;
        this.model.Latitude = response.data.Latitude;
        this.model.Longitude = response.data.Longitude;
      } catch (e) {
        // Do nothing
      }
    },
    searchCountries(title) {
      const results = this.countries.items.filter((e) => e.Title === title);
      return results.length ? results[0].Code : null;
    },
    openScheduleCreator(model, prop) {
      this.schedules.tmp = { model, prop };
      this.$refs.ScheduleForm.open();
    },
    addNewCountry(response) {
      this.countries.items.push(response);
      this.model.Country = response.Code;
    },
    addNewSechedule(response) {
      this.schedules.items.push(response);
      this.schedules.tmp.model[this.schedules.tmp.prop] = response.Id;
      this.schedules.tmp = null;
    },
    removeItem(set, index) {
      this.$delete(set, index);
    },
    getOfficeModel() {
      return { Title: '', Description: '', Phone: '' };
    },
  },
  created() {
    this.$store.dispatch('get', 'Country').then((response) => {
      this.countries.items = response.data;
      this.model.Country = this.searchCountries(this.Country);
    }).finally(() => {
      this.countries.loading = false;
    });

    this.$store.dispatch('get', 'Schedule').then((response) => {
      this.schedules.items = response.data;
    }).finally(() => {
      this.schedules.loading = false;
    });

    this.$store.dispatch('get', 'Tag/location').then((response) => {
      this.tags.items = response.data;
    }).finally(() => {
      this.tags.loading = false;
    });
  },
  mounted() {
    if (!this.canEdit()) {
      const heading = this.model.Status === 1
        ? 'Under Review'
        : 'Readonly Mode';
      const msg = this.model.Status === 1
        ? 'This Location is currently under review. No changes may be made at this time.'
        : 'This Location has been approved and published. Please see an Editor or Administrator if you would like to make any changes.';
      this.$emit('alert', heading, msg);
    }
  },
};
</script>
